import { useEffect, useState } from 'react';
import { Popup, useMap } from 'react-map-gl';
import type mapboxGl from 'mapbox-gl';
import { formatNumber } from 'utils/number';
import style from './tc-composite-tangent-tooltip.module.scss';

interface TangentHover {
  longitude: number;
  latitude: number;
  text: string;
}

/**
 * The mock's sticky tooltip on a composite wellbore's tangents, following the
 * cursor: "{name} · tangent {N} ft, not perforated". Legs on the same layer get
 * none.
 *
 * @param {object} props - Props.
 * @param {string} props.layerId - The line layer holding the composite features.
 * @return {JSX.Element | null} The tooltip while a tangent is hovered.
 */
export const TcCompositeTangentTooltip = ({ layerId }: { layerId: string }) => {
  const { current: mapRef } = useMap();
  const [hover, setHover] = useState<TangentHover | null>(null);

  useEffect(() => {
    if (!mapRef) return undefined;
    const map = mapRef.getMap();
    const onMove = (e: mapboxGl.MapLayerMouseEvent) => {
      const props = e.features?.find(
        (f) => f.properties?.kind === 'tangent'
      )?.properties;
      const lengthFt = Number(props?.lengthFt);
      if (!props || !Number.isFinite(lengthFt)) {
        setHover(null);
        return;
      }
      setHover({
        longitude: e.lngLat.lng,
        latitude: e.lngLat.lat,
        text: `${props.name} · tangent ${formatNumber(
          lengthFt,
          0,
          0
        )} ft, not perforated`,
      });
    };
    const onLeave = () => setHover(null);
    map.on('mousemove', layerId, onMove);
    map.on('mouseleave', layerId, onLeave);
    return () => {
      map.off('mousemove', layerId, onMove);
      map.off('mouseleave', layerId, onLeave);
    };
  }, [mapRef, layerId]);

  if (!hover) return null;
  return (
    <Popup
      longitude={hover.longitude}
      latitude={hover.latitude}
      offset={12}
      closeButton={false}
      closeOnClick={false}
      maxWidth="none"
      className={style.tangentTooltip}
    >
      {hover.text}
    </Popup>
  );
};
